import React, { useContext, useEffect } from "react";

import {
  Grid,
  Paper,
  Avatar,
  Typography,
  Divider,
  Box,
  CircularProgress,
} from "@material-ui/core";

import TodayIcon from "@material-ui/icons/Today";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import EventNoteIcon from "@material-ui/icons/EventNote";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import WarningIcon from "@material-ui/icons/Warning";
import PaymentIcon from "@material-ui/icons/Payment";

import { DashboardContext } from "../../../context/states/dashboardState";
import { useStyles } from "./styles";

const FinancialDashboard = () => {
  const classes = useStyles();

  const {
    getFinancialDashboardHandler,
    financialDashboard,
    financialDashboardLoading,
  } = useContext(DashboardContext);

  useEffect(() => {
    getFinancialDashboardHandler();
  }, []);

  const formatAmount = (amount) => {
    const value = Number(amount);

    if (!Number.isFinite(value)) {
      return "0.00";
    }

    return value.toFixed(2);
  };

  const clientInvoices = financialDashboard?.clientInvoices || {};
  const outstanding = clientInvoices?.outstanding || {};

  const interpreterInvoices =
    financialDashboard?.interpreterInvoices?.totalPayable || {};

  const clientInvoiceCards = [
    {
      name: "Today's Invoices",
      count: clientInvoices?.today?.count || 0,
      amount: clientInvoices?.today?.amount || 0,
      icon: <TodayIcon />,
      className: "booking",
    },
    {
      name: "This Month's Invoices",
      count: clientInvoices?.thisMonth?.count || 0,
      amount: clientInvoices?.thisMonth?.amount || 0,
      icon: <CalendarTodayIcon />,
      className: "confirmed",
    },
    {
      name: "Last Month's Invoices",
      count: clientInvoices?.lastMonth?.count || 0,
      amount: clientInvoices?.lastMonth?.amount || 0,
      icon: <EventNoteIcon />,
      className: "quotation",
    },
  ];

  const outstandingCards = [
    {
      name: "Total Outstanding",
      count: outstanding?.total?.count || 0,
      amount: outstanding?.total?.amount || 0,
      icon: <AccountBalanceWalletIcon />,
      className: "danger",
    },
    {
      name: "Less Than 30 Days",
      count: outstanding?.lessThan30Days?.count || 0,
      amount: outstanding?.lessThan30Days?.amount || 0,
      icon: <AccessTimeIcon />,
      className: "approved",
    },
    {
      name: "30 - 60 Days",
      count: outstanding?.thirtyToSixtyDays?.count || 0,
      amount: outstanding?.thirtyToSixtyDays?.amount || 0,
      icon: <WarningIcon />,
      className: "warning",
    },
    {
      name: "More Than 60 Days",
      count: outstanding?.moreThan60Days?.count || 0,
      amount: outstanding?.moreThan60Days?.amount || 0,
      icon: <WarningIcon />,
      className: "cancel",
    },
  ];

  if (financialDashboardLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={1}>
      {/* =========================================
          CLIENT INVOICES
      ========================================== */}

      <Grid item xs={12}>
        <Typography variant="h6">Client Invoice Overview</Typography>
      </Grid>

      {clientInvoiceCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Paper elevation={1} className={classes.paper}>
            <div className={classes.nameDiv}>
              <Typography variant="h6">{card.name}</Typography>
              <Typography variant="h4" className={classes.amount}>
                £ {formatAmount(card.amount)}
              </Typography>

              <Typography className={classes.count}>
                Count: {card.count} invoices
              </Typography>
            </div>

            <Avatar className={classes[card.className]}>{card.icon}</Avatar>
          </Paper>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* =========================================
          OUTSTANDING INVOICES
      ========================================== */}

      <Grid item xs={12}>
        <Typography variant="h6">Outstanding Invoices</Typography>
      </Grid>

      {outstandingCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Paper elevation={1} className={classes.paper}>
            <div className={classes.nameDiv}>
              <Typography variant="h6">{card.name}</Typography>

              <Typography className={classes.amount}>
                £ {formatAmount(card.amount)}
              </Typography>

              <Typography variant="h4" className={classes.count}>
                Count: {card.count} invoices
              </Typography>
            </div>

            <Avatar className={classes[card.className]}>{card.icon}</Avatar>
          </Paper>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* =========================================
          INTERPRETER INVOICE
      ========================================== */}

      <Grid item xs={12}>
        <Typography variant="h6">Interpreter Invoice Overview</Typography>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Paper elevation={1} className={classes.paper}>
          <div className={classes.nameDiv}>
            <Typography variant="h6">Total Payable Amount</Typography>

            <Typography variant="h4" className={classes.amount}>
              £ {formatAmount(interpreterInvoices?.amount)}
            </Typography>

            <Typography className={classes.count}>
              Count: {interpreterInvoices?.count || 0} invoices
            </Typography>
          </div>

          <Avatar className={classes.paid}>
            <PaymentIcon />
          </Avatar>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default FinancialDashboard;
